import React, { useEffect, useRef, useState } from "react";
import { generateReceipt } from "./Content/pdfMaker";
import { Button, Flex, Box, Container, Heading, Stack, Center, Image, VStack, Input } from "@chakra-ui/react";
import { ReceiptCard, getRandomVariant, variants } from "./ConfirmationTemplates";
import { colorMain } from "../constants/themes";
import { handleGetReceiptDetails } from "../utils/api";
import CONFIRMATION_TEMPLATES from "../data/confirmationTemplates";
import { createFileName, useScreenshot } from "use-react-screenshot";
import { useNavigate, useParams } from "react-router";
import LoadingPage from "./LoadingPage";
import FourOFour from "./404";
import { ToastContainer, toast } from "react-toastify";

import { Text, Circle, Spinner, Card, Icon } from "@chakra-ui/react";
import { WarningIcon, SearchIcon } from "@chakra-ui/icons";
import PaymentConfirmation from "../components/Features/PaymentConfirmation";
import {
  formatterPaidBy,
  formatterTimePaid,
  titleCase,
} from "../utils/functions";
import { AiFillCheckCircle } from "react-icons/ai";
import collectLogo from "../assets/img/nobukcollect.png";

const ReceiptDownload = () => {
  const navigate = useNavigate();
  const { mpesaCode } = useParams();
  const [processing, setProcessing] = useState(false);
  const [data, setData] = useState(false);
  const [load, setLoad] = useState(true);
  const [clicked, setClicked] = useState(false);
  const [time, setTime] = useState(100);
  const [searchMpesaCode, setSearchMpesaCode] = useState("");
  setTimeout(() => {
    setTime(0);
  }, 60000);
  const ref = useRef(null);
  const refs = useRef(null);
  const initialRef = useRef();

  const [image, takeScreenshot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });

  const [downloading, setIsDownloading] = useState(false);
  const [receipt, setReceipt] = useState(true);

  // Defaults to Neo-Brutalist unless the collection's alpha_type picks a specific style
  const alphaType = data?.alpha_type || data?.sale_settings?.alpha_type;
  const variant = variants.includes(alphaType) ? alphaType : "neo-brutalist";
  const templateHtml = CONFIRMATION_TEMPLATES[variant];

  const zenColor = "#062a30";

  useEffect(() => {
    const fetchDetails = async () => {
      if (mpesaCode) {
        setClicked(true);
        toast.info("verifying payment!");
        const result = await handleGetReceiptDetails(mpesaCode);
        if (result.success) {
          setData(result.data);
          toast.success(result.message);
        } else {
          toast.warning(result.message);
        }
        setClicked(false);
        setLoad(false);
      } else {
        setLoad(false);
      }
    };
    fetchDetails();
  }, [mpesaCode]);

  useEffect(() => {
    if (!mpesaCode) {
      initialRef?.current?.focus();
      toast.warning(`Please enter an MPESA code to search!`);
    }
  }, []);

  const getImage = async () => {
    if (receipt) {
      setReceipt(false);
      return;
    }

    setIsDownloading(true);

    try {
      const dataUri = await takeScreenshot(refs.current);
      downloadImage(dataUri);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }

    setIsDownloading(false);
    setReceipt(true);
  };

  useEffect(() => {
    if (!receipt) {
      getImage();
    }
  }, [receipt]);

  const getThisImage = async () => {
    setIsDownloading(true);
    try {
      await takeScreenshot(refs.current);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }
    setIsDownloading(false);
  };

  const downloadImage = (dataUri) => {
    setIsDownloading(true);
    const link = document.createElement("a");
    link.href = dataUri;
    link.download = "receipt.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsDownloading(false);
  };

  const handleSearch = () => {
    if (searchMpesaCode.trim()) {
      navigate(`/receipt/${searchMpesaCode.trim()}`);
    } else {
      toast.error("Please enter a valid Mpesa code.");
    }
  };

  let search = (
    <>
      <ToastContainer />
      <Box
        bg="white"
        borderRadius="2xl"
        shadow="xl"
        p={6}
        position="relative"
        overflow="hidden"
      >
        <Flex alignItems="center" justifyContent="center" mb={4}>
          <Circle
            bg="gray.50"
            size="16"
          >
            <SearchIcon fontSize="24px" color={zenColor} />
          </Circle>
        </Flex>
        <Text fontWeight="bold" fontSize="xl" textAlign="center" color={zenColor} mb={2}>
          Get Your Payment Receipt!
        </Text>
        <Text fontSize="md" color="gray.500" textAlign="center" mb={6}>
          Please enter the MPESA confirmation code of the payment you made
        </Text>
        <Flex
          flexDirection="column"
          alignItems="center"
          gap={4}
        >
          <Input
            placeholder="Enter Mpesa Code"
            value={searchMpesaCode}
            onChange={(e) => setSearchMpesaCode(e.target.value)}
            size="lg"
            borderColor="gray.300"
            borderRadius="md"
            focusBorderColor={zenColor}
            ref={initialRef}
          />
          <Button
            bg={zenColor}
            color="white"
            size="lg"
            width="full"
            borderRadius="md"
            _hover={{ bg: "#002a35" }}
            onClick={handleSearch}
            isDisabled={!searchMpesaCode || (searchMpesaCode && searchMpesaCode?.length !== 10)}
            fontWeight="bold"
          >
            Search
          </Button>
        </Flex>
      </Box>
    </>
  );

  return (
    <Box minH="100vh" bg="#fbfdfd" py={8} px={4} fontFamily="'Poppins', sans-serif" display="flex" alignItems="center" justifyContent="center">
      {data && (
        <Container maxW="md" p={0}>
          <ToastContainer />
          {clicked ? (
            <Flex
              h="300px"
              flexDir="column"
              alignItems="center"
              justifyContent="center"
              bg="white"
              borderRadius="3xl"
              shadow="2xl"
            >
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color={zenColor}
                size="xl"
              />
              <Text fontSize="md" mt={4} color="gray.600" fontWeight="medium">Verifying payment...</Text>
            </Flex>
          ) : (
            <VStack spacing={6} w="100%">
              <Box w="100%" maxW="md" ref={refs}>
                <Center mb={4}>
                  <Image src={collectLogo} w="100px" alt="Nobuk Collect" />
                </Center>
                <ReceiptCard
                  title="RECEIPT"
                  html={templateHtml}
                  data={{
                    amount: data?.amount !== undefined
                      ? Number(data?.amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      : "0.00",
                    currency: "KES",
                    paidBy: titleCase(formatterPaidBy(data?.paid_by) || "Anonymous"),
                    fromAccount: data?.phone || "N/A",
                    transactionCode: data?.transcode || mpesaCode,
                    date: data?.time_paid ? formatterTimePaid(data?.time_paid) : "Just now",
                    baseImage: data?.base_image || data?.sale_settings?.base_image,
                    customerMessage: data?.customer_message || data?.sale_settings?.customer_message,
                  }}
                />
              </Box>

              <VStack w="100%" spacing={4}>
                <Button
                  bg="#062a30"
                  color="white"
                  size="lg"
                  width="full"
                  py={7}
                  borderRadius="xl"
                  _hover={{ bg: "#000000", transform: "translateY(-1px)", shadow: "lg" }}
                  _active={{ bg: "#062a30", transform: "translateY(0)" }}
                  fontSize="md"
                  fontWeight="semibold"
                  isDisabled={processing || downloading}
                  isLoading={processing || downloading}
                  loadingText="Processing..."
                  onClick={() => {
                    if (data && data.transcode) {
                      generateReceipt({
                        phoneNumberBy: data.phone || "2547********",
                        paidBy: data.paid_by || "Unknown",
                        amount: data?.amount,
                        transactionCode: data?.transcode || mpesaCode,
                        orgName: data?.collection_name || "Unknown",
                        selection: data?.["Paid For"] || "Unknown",
                        setProcessing,
                        time_paid: data?.time_paid,
                      });
                    } else {
                      toast.update("Please refresh and try again!");
                    }
                  }}
                >
                  Get Proof of Payment
                </Button>

                <Button
                  variant="ghost"
                  width="full"
                  color="gray.400"
                  size="sm"
                  fontWeight="normal"
                  onClick={() => getImage()}
                  isDisabled={downloading}
                >
                  Download as Image
                </Button>
              </VStack>
            </VStack>
          )}
        </Container>
      )}

      {!mpesaCode && (
        <Container maxW="md" p={0}>
          {search}
        </Container>
      )}

      {(load || time === 60000) && <LoadingPage />}

      {!load && !data && time === 0 && mpesaCode && <FourOFour />}
      {!load && !data && time !== 0 && mpesaCode && (
        <Container maxW="md" p={0}>
          <ToastContainer />
          <Box
            bg="white"
            borderRadius="3xl"
            shadow="2xl"
            overflow="hidden"
            p={8}
            textAlign="center"
          >
            <Flex justifyContent="center" mb={6}>
              <Circle bg="red.50" size="20">
                <WarningIcon fontSize="40px" color="red.500" />
              </Circle>
            </Flex>
            <Heading size="lg" color="#062a30" mb={2}>
              Payment Not Found
            </Heading>
            <Text color="gray.500" fontSize="sm" mb={4}>
              We couldn't find a payment with the provided code. Please verify and try again.
            </Text>
          </Box>
        </Container>
      )}
    </Box>
  );
};
export default ReceiptDownload;
